"use client";

import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";
import { useLanguage } from "@/context/LanguageContext";

export default function AdventureHeroSection() {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1658107977958-2b313d67d112"
          alt="Vast Tunisian desert landscape with golden sand dunes under blue sky at sunset"
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/70 via-foreground/50 to-foreground/70"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-32 right-10 w-40 h-40 rounded-full overflow-hidden opacity-60 animate-float hidden lg:block">
        <AppImage
          src="https://images.unsplash.com/photo-1601614740250-095f32be3cf0"
          alt="Person riding quad bike through desert sand creating dust trail"
          className="w-full h-full object-cover" />
      </div>

      <div className="absolute bottom-32 left-10 w-48 h-48 rounded-full overflow-hidden opacity-50 animate-float hidden lg:block [animation-delay:2s]">
        <AppImage
          src="https://images.unsplash.com/photo-1691394792846-122c61efd0d5"
          alt="Camel caravan walking through desert at golden hour with riders"
          className="w-full h-full object-cover" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Main Heading */}
          <div className="mb-8 reveal will-animate">
            <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-bold leading-none tracking-tight text-white mb-4">
              {t("adv_hero_title")}
              <span className="block mt-2 gradient-text">{t("adv_hero_subtitle")}</span>
            </h1>
          </div>

          {/* Subheading */}
          <div className="mb-12 reveal will-animate">
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {t("adv_hero_desc")}
              <span className="font-semibold text-white">{t("adv_hero_desc_bold")}</span>
              {t("adv_hero_desc_end")}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center reveal will-animate">
            <a
              href="#experiences"
              className="group px-8 py-4 bg-accent text-white rounded-full font-semibold text-lg hover:bg-accent/90 transition-all hover:scale-105 shadow-lg flex items-center gap-2">
              <span>{t("adv_hero_book")}</span>
              <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="https://wa.me/21654604948"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-success text-white rounded-full font-semibold text-lg hover:bg-success/90 transition-all hover:scale-105 shadow-lg flex items-center gap-2">
              <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
              <span>{t("adv_hero_whatsapp")}</span>
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 reveal will-animate">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Icon name="ShieldCheckIcon" size={32} className="text-white" />
              </div>
              <p className="text-sm font-semibold text-white">{t("adv_hero_guides")}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Icon name="UserGroupIcon" size={32} className="text-white" />
              </div>
              <p className="text-sm font-semibold text-white">{t("adv_hero_family")}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Icon name="ClockIcon" size={32} className="text-white" />
              </div>
              <p className="text-sm font-semibold text-white">{t("adv_hero_flexible")}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Icon name="MapPinIcon" size={32} className="text-white" />
              </div>
              <p className="text-sm font-semibold text-white">{t("adv_hero_pickup")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDownIcon" size={32} className="text-white" />
      </div>
    </section>
  );
}